import React, { useState } from "react";
import {
  FieldType,
  FieldTypeType,
  OptionType,
} from "../../../services/helpers/types";
import { Divider } from "@mui/material";
import DraggableSelectOptionsList from "../DraggableSelectOptionsList/DraggableSelectOptionsList";
import ButtonGroupField from "../../molecules/ButtonGroupField/ButtonGroupField";
import { useTranslation } from "react-i18next";

interface AddButtonGroupFieldProps {
  field?: FieldType;
  onFieldChange: (name: string, value?: any, defaultValue?: string) => void;
}

const AddButtonGroupField = (props: AddButtonGroupFieldProps) => {
  const { field, onFieldChange } = props;

  const { t } = useTranslation();

  const [defaultValue, setDefaultValue] = useState<string>(
    field?.defaultValue ?? `${t("option")} 1`
  );

  const [newOptions, setNewOptions] = useState<OptionType[]>(
    field?.options ?? [
      {
        name: `${t("option")} 1`,
        value: `${t("option")} 1`,
        position: 1,
        optionID: 0,
        fieldID: 0,
      },
      {
        name: `${t("option")} 2`,
        value: `${t("option")} 2`,
        position: 2,
        optionID: 1,
        fieldID: 0,
      },
    ]
  );

  const [customField, setCustomField] = useState<FieldType | undefined>(
    field ?? {
      fieldID: 0,
      name: "Možnosti",
      position: 0,
      type: FieldTypeType.ButtonGroup,
      noteOption: false,
      defaultValue: "",
      options: newOptions,
      tabID: 0,
      placeholder: "",
      value: "",
    }
  );

  const handleDefaultValueChange = (value?: string) => {
    setDefaultValue(value ?? "");
    onFieldChange("defaultValue", value);
  };

  const setDefaultValueSelectOptions = (
    options: OptionType[],
    defaultValue?: string
  ) => {
    if (customField) {
      setCustomField({ ...customField, options: options });
    } else {
      setCustomField({
        fieldID: 0,
        name: "Možnosti",
        position: 0,
        type: FieldTypeType.ButtonGroup,
        noteOption: false,
        defaultValue: "",
        options: options,
        tabID: 0,
        placeholder: "",
        value: "",
      });
    }
    onFieldChange("options", options, defaultValue);
  };

  const handleDelete = (itemID: number) => {
    const deleteNewOptions = newOptions
      .filter((option) => option.position !== itemID)
      .map((value, index) => {
        return { ...value, position: index + 1 };
      });

    setDefaultValueSelectOptions(deleteNewOptions);
    setNewOptions(deleteNewOptions);
  };

  const setStateNewOptions = (option: OptionType, defaultValue?: string) => {
    const changeNewOptions = newOptions.map((value) => {
      if (value.position === option.position) {
        return option;
      }
      return value;
    });

    setDefaultValueSelectOptions(changeNewOptions, defaultValue);
    setNewOptions(changeNewOptions);
  };

  const handleOptionChange = (option: OptionType) => {
    const position = option.position;
    const oldOption = newOptions.find((value) => value.position === position);

    // if option is already selected and value is changed, change default value too
    if (oldOption?.value === defaultValue) {
      setDefaultValue(option.value);
      setStateNewOptions(option, option.value);
      return;
    }
    setStateNewOptions(option);
  };

  const handlePositionChange = (newItemsPositions: number[]) => {
    const reorderedNewOptions: OptionType[] = newItemsPositions.map(
      (position, index) => {
        const newOption = newOptions.find(
          (option) => option.position === position
        );
        if (!newOption) {
          return newOptions[index];
        }
        return { ...newOption, position: index + 1 };
      }
    );
    setNewOptions(reorderedNewOptions);
    onFieldChange("options", reorderedNewOptions);
  };

  return (
    <div className="dui-add-button-group-field">
      <Divider className="dui-divider" />
      <div className="dui-add-button-group-field-values">
        <div className="dui-add-button-group-field-values-title">
          {t("selectOptions")}
        </div>
        <DraggableSelectOptionsList
          options={newOptions}
          onDeleteItem={handleDelete}
          onOptionChange={handleOptionChange}
          onChangePosition={handlePositionChange}
        />
      </div>
      <Divider className="dui-divider" />
      <div className="dui-example-field">
        <div className="dui-example-field-title">{t("defaultValue")}:</div>
        <ButtonGroupField
          field={customField}
          value={defaultValue}
          onChange={handleDefaultValueChange}
        />
      </div>
    </div>
  );
};

export default AddButtonGroupField;
